<template>
  <div>
    <v-dialog
      v-if="$vuetify.breakpoint.mdAndUp"
      v-model="value"
      max-width="45%"
      @click:outside="$emit('input', false)"
    >
      <v-card>
        <v-toolbar
          dark
          color="primary"
          style="height:40px;"
          flat
        >
          <v-spacer />
          <v-btn
            icon
            dark
            aria-label="close"
            class="mt-0 ml-2 mr-3"
            @click.native="$emit('input', false)"
          >
            <v-icon>sn-icon-exit</v-icon>
          </v-btn>
        </v-toolbar>
        <v-layout
          :style="{'height:100px;': !showLargeNameText(person.full_name), 'height:120px;': showLargeNameText(person.full_name)}"
          row
          class="contact--header primary px-3"
        >
          <v-flex
            xs9
            offset-xs4
          >
            <h3
              :class="{'display-1 mt-3': !showLargeNameText(person.full_name),'display-3': showLargeNameText(person.full_name)}"
              class="light ml-4 white--text"
            >
              {{ person.full_name }}
            </h3>
            <p class="ml-4 white--text">
              {{ person.title }}
              <span v-if="formattedNMLS">
                | {{ formattedNMLS }}
              </span>
            </p>
          </v-flex>
        </v-layout>
        <v-layout
          row
          class="pt-3"
          style="max-width: 100%;"
        >
          <v-flex xs3>
            <div
              :class="{'largeTextAvatarImage': hasLongName(person.full_name), 'smallTextAvatarImage': !hasLongName(person.full_name)}"
              class="avatarImage"
            >
              <v-avatar
                :size="avatarSquareSize"
                :color="profileImageUrl ? '' : 'primary'"
                class="darken-2"
              >
                <img
                  v-if="profileImageUrl"
                  :src="profileImageUrl"
                  alt="profile picture"
                  @error="imageAltUrl"
                >
                <span
                  v-else
                  class="white--text sn-display-3 sn-font-weight-medium sn-font-standard"
                >
                  {{ person.initials }}
                </span>
              </v-avatar>
            </div>
          </v-flex>
          <v-flex
            class="mb-4"
            xs8
            offset-xs1
            style="word-wrap: break-word;"
          >
            <p class="subheading ml-4">
              <span v-if="person.unformatted_phone">
                <span>M: </span>
                <a
                  :href="'tel:+1' + person.unformatted_phone"
                  class="no-underline"
                >
                  {{ person.phone }}
                </a>
              </span>
              <br>
              <span v-if="person.unformatted_office_phone">
                <span>O: </span>
                <a
                  :href="'tel:+1' + person.unformatted_office_phone"
                  class="no-underline"
                >
                  {{ person.office_phone }}
                </a>
              </span>
              <br>
              <a
                :href="'mailto:' + person.email"
                class="no-underline"
              >
                {{ person.email }}
              </a>
              <br>
              <a
                :href="person.website"
                class="no-underline"
              >
                {{ person.website }}
              </a>
            </p>
            <p class="ml-4">
              {{ addressLineOne() }}
              <br>
              {{ addressLineTwo() }}
            </p>
            <template
              v-if="person.effective_web_links && person.effective_web_links.length !== 0"
            >
              <div class="mb-4">
                <span
                  v-for="link in person.effective_web_links"
                  id="personWebLinks"
                  :key="link.id"
                  class="ml-4"
                >
                  <a
                    :href="link.url"
                    target="_blank"
                    class="no-underline"
                  >
                    {{ getWebLinkText(link) }}
                  </a>
                  <br :key="'br-' + link.id">
                </span>
              </div>
              <template>
                <a
                  v-for="link in person.social_links"
                  :key="link.id"
                  :href="link.url"
                  :aria-label="link.url"
                  target="_blank"
                  class="ml-4 no-underline"
                >
                  <v-icon>{{ `sn-icon-${ linkIcon( link ) }` }}</v-icon>
                </a>
              </template>
            </template>
          </v-flex>
        </v-layout>
      </v-card>
    </v-dialog>
    <!-- For small and down screens, this full screen dialog appears instead of the "business card" popup -->
    <v-dialog
      v-else
      v-model="value"
      :overlay="false"
      fullscreen
      transition="dialog-bottom-transition"
    >
      <v-card>
        <v-toolbar
          dark
          color="primary"
          style="height:60px;"
        >
          <v-btn
            icon
            dark
            aria-label="close"
            @click.native="$emit('input', false)"
          >
            <v-icon>sn-icon-exit</v-icon>
          </v-btn>
          <v-toolbar-title>{{ person.full_name }}</v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <v-row>
            <v-col
              cols="2"
            >
              <v-avatar
                :size="avatarSquareSize"
                color="primary"
              >
                <img
                  v-if="profileImageUrl"
                  :src="profileImageUrl"
                  alt="profile picture"
                  @error="imageAltUrl"
                >
                <span
                  v-else
                  class="white--text sn-display-3 sn-font-weight-medium sn-font-standard"
                >
                  {{ person.initials }}
                </span>
              </v-avatar>
            </v-col>
            <v-col
              style="margin-left: 24px;"
              cols="9"
            >
              <h5 class="headline headline mb-1">
                {{ person.full_name }}
              </h5>
              <h2 class="subheading">
                {{ person.title || 'Loan Officer' }}
              </h2>
            </v-col>
          </v-row>
          <v-layout
            row
            class="mt-2"
          >
            <v-flex xs12>
              <p class="title mb-1">
                Contact
              </p>
              <p class="subheading mb-1">
                <a :href="'tel:+1' + person.unformatted_phone">{{ person.phone }}</a>
              </p>
              <p class="subheading mb-1">
                <a :href="'mailto:' + person.email">{{ person.email }}</a>
              </p>
              <p>
                {{ addressLineOne() }}
                <br>
                {{ addressLineTwo() }}
              </p>
              <p class="title mb-1 mt-4">
                Resources
              </p>
              <p class="subheading mb-1">
                <a :href="person.website">
                  My website
                </a>
              </p>
              <p
                v-for="link in person.effective_web_links"
                :key="link.id"
                class="subheading mb-1"
              >
                <a
                  :href="link.url"
                  target="_blank"
                >
                  {{ getWebLinkText(link) }}
                </a>
              </p>
              <template>
                <a
                  v-for="link in person.social_links"
                  :key="link.id"
                  :href="link.url"
                  :aria-label="link.url"
                  target="_blank"
                  class="ml-4 no-underline"
                >
                  <v-icon>{{ `sn-icon-${ linkIcon( link ) }` }}</v-icon>
                </a>
              </template>
              <p
                v-if="formattedNMLS"
                class="subheading mb-1"
              >
                {{ formattedNMLS }}
              </p>
            </v-flex>
          </v-layout>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { formatNMLS } from '../Shared/formatNMLS.js'

export default {
  name: 'ContactInfoDialog',
  props: {
    person: {
      type: Object,
      default: () => {},
      required: true
    },
    value: {
      type: Boolean,
      default: false,
      required: true
    }
  },
  computed: {
    profileImageUrl () {
      return this.person?.profile_image_url
    },
    formattedNMLS () {
      return formatNMLS(this.person?.license)
    },
    avatarSquareSize () {
      if (this.$vuetify.breakpoint.xl) return '194'
      if (window.innerWidth >= 1264 && window.innerWidth < 1400) return '150'
      if (this.$vuetify.breakpoint.lg) return '194'
      if (this.$vuetify.breakpoint.md) return '130'
      if (this.$vuetify.breakpoint.smAndDown) return '68'
      return ''
    }
  },
  methods: {
    imageAltUrl (event) {
      event.target.src = 'https://simplenexus.com/images/profile_sihlouette.png'
    },
    addressLineOne () {
      let firstLine = ''
      if (this.person.street1) firstLine += this.person.street1
      if (this.person.street2) firstLine += ', ' + this.person.street2
      return firstLine
    },
    getWebLinkText (link) {
      return link.link_type ? link.link_type.name : link.link_text || 'Web Link'
    },
    addressLineTwo () {
      let secondLine = ''
      if (this.person.city) secondLine += this.person.city + ', '
      if (this.person.state) secondLine += this.person.state
      if (this.person.zip) secondLine += ' ' + this.person.zip
      return secondLine
    },
    hasLongName (name) {
      if (name) {
        if (name.length >= 12) return true
        if (name.length < 12) return false
      }
      return false
    },
    showLargeNameText (name) {
      if (this.hasLongName(name)) return false
      if (this.hasLongName(name) && this.$vuetify.breakpoint.lgAndUp) return true
      return false
    },
    linkIcon (link) {
      const icons = {
        facebook: 'facebook',
        google: 'google-plus',
        linkedin: 'linkedin',
        twitter: 'twitter',
        yelp: 'yelp',
        youtube: 'youtube',
        ie: 'link',
        rss: 'link',
        home_search: 'link',
        local_coupons: 'link',
        trulia: 'trulia',
        zillow: 'zillow',
        pinterest: 'pinterest',
        instagram: 'instagram',
        path2own: 'link',
        preflight: 'link',
        prequalified: 'link',
        licensing: 'link',
        'income-analysis': 'link',
        loan_status: 'link',
        home_scouting: 'link',
        film: 'link'
      }
      return icons[link?.link_type?.icon_name] || 'link'
    },
    formatNMLS
  }
}
</script>
<style scoped>
.smallTextAvatarImage {
  margin-left:24px;
  margin-top: -115px;
}
.largeTextAvatarImage {
  margin-left:24px;
  margin-top: -105px;
}
.contact--header {
  margin: 0;
}
.no-underline {
  text-decoration: none;
}
</style>
